import UserInfo from "../../session/UserInfo";
import { NewrelicBrowser } from "../../type";
import { logError } from "./clientUtilsNR";
import { ReporterI } from "./Reporter";

const SESSION_ID_TIMEOUT = 20000;
const userInfo = new UserInfo();

// kicks off fetching of sessionId so it's available to metrics
userInfo
    .getSessionId(SESSION_ID_TIMEOUT)
    .catch((err) => logError(err, { msg: "Failed to get sessionId for PerformanceMonitor" }));

export interface NewRelicBrowserReporterProps {
    module: string;
    action: string;
    containerId: string;
    position: string;
}

export default class NewRelicBrowserReporter implements ReporterI {

    name: string;
    startTime: number = Date.now();
    sessionId: string | undefined | null = null;
    details: NewRelicBrowserReporterProps;
    disabled = true;
    newrelic: NewrelicBrowser;
    sessionStartTime: number | undefined;

    constructor(details: NewRelicBrowserReporterProps) {
        this.disabled = typeof window.NREUM !== "object";
        this.details = details;
        this.newrelic = window.NREUM;
        this.name = this._composeName(details);
        this.sessionId = userInfo.getSessionIdSync();
    }

    /**
     * Creates a mark for beginning of measure
     */
    start() {
        if (this.disabled) return;

        /**
         * Records absolute start time since beginning of epoch
         * @type {number}
         */
        this.startTime = Date.now();
        /**
         * Records relative start time since beginning of session
         * @type {DOMHighResTimeStamp}
         */
        this.sessionStartTime = performance.now();
    }

    /**
     * Creates a mark for end of measure
     */
    end(detail?: Record<string, unknown>) {
        if (this.disabled || !this.startTime) {
            return;
        }

        this._measure(detail);
    }

    /**
     * Marks a single event in time
     */
    mark(action: string, attributes?: Record<string, unknown>) {
        if (this.disabled) return;

        const { position } = this.details;

        this.newrelic.addPageAction(`${ this.name }.${ action }`, {
            sessionId: this.sessionId,
            position,
            ...attributes
        });
    }

    /**
     * Measures duration between two markers
     * @param {object} [detail]
     * @private
     */
    _measure(detail?: Record<string, unknown>) {
        const endTime = Date.now();
        const duration = endTime - this.startTime;
        const traceObject = {
            name: this.name,
            start: this.startTime, // Time in ms since epoch
            end: endTime
        };

        const { action, position, module } = this.details;

        this.newrelic.addToTrace(traceObject);
        this.newrelic.addPageAction(this.name, {
            ...detail,
            action,
            duration,
            module,
            position,
            startTime: this.sessionStartTime,
            sessionId: this.sessionId
        });
    }

    /**
     * Generates generic event name
     */
    _composeName(options: NewRelicBrowserReporterProps) {
        return `${ /^Sparkle/.test(options.module) ? options.module : `Sparkle:${ options.module }` }.${ options.action }${ options.position ? `-${ options.position }` : "" }`;
    }

}
